import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";
import { selectUserImpersonation } from "@user-management-context/write/application/use-cases/impersonate-user/selectors/impersonate-user-selectors";
import {
  resetUserImpersonation,
  stopImpersonation,
} from "@user-management-context/write/application/use-cases/impersonate-user/impersonate-user";
import { terminateSession } from "@user-management-context/write/application/use-cases/session/terminate-session";
import { returnToAdminSession } from "@user-management-context/write/application/use-cases/session/return-to-admin-session";
import { resetImpersonationExpired } from "@user-management-context/write/application/use-cases/session/session-reducer";
import { clearImpersonationMarker } from "@shared-kernel/core/impersonation/impersonation-marker";
import { resolveLandingUrl } from "@user-management-context/shared/domain/landing-url";
import { useSession } from "@user-management-context/primary/hooks/use-session";

export const useImpersonation = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { fetching, data: impersonationData } = useAppSelector(selectUserImpersonation);
  const { user: userInfo, status, activeRole, impersonatedBy, impersonationExpired } = useSession();
  const isImpersonating = impersonatedBy !== null;

  useEffect(() => {
    if (fetching === "success" && impersonationData) {
      const { activeRole } = impersonationData;
      const target = resolveLandingUrl({ activeRole, isAdmin: false });

      toast({ description: "Impersonation réussie" });
      if (target) navigate(target);
      dispatch(resetUserImpersonation());
    } else if (fetching === "failed") {
      toast({
        description: "Erreur lors de l'impersonation. Veuillez réessayer.",
        variant: "destructive",
      });
      dispatch(resetUserImpersonation());
    }
  }, [fetching, impersonationData, toast, navigate, dispatch]);

  // The third way an impersonation can end, next to the two above: on its own.
  // The token has a hard expiry and no refresh token, so when it lapses the admin's
  // own refresh token quietly mints an admin access token on the next request —
  // leaving the admin back on their own account, on a page that is no longer
  // theirs, with nothing said. Land it exactly where leaving on purpose would.
  useEffect(() => {
    if (!impersonationExpired) return;
    // Wait for the sync to land: until then the admin's own profile is not loaded
    // and the router still serves the impersonated user's routes — navigating
    // there would only flash a 404 on the way.
    if (status !== "active" || !userInfo) return;

    toast({
      description: "Session d'impersonation expirée. Vous êtes de retour sur votre compte administrateur.",
      variant: "destructive",
    });
    // Only now: the marker is what makes the next sync raise the flag again, so it
    // must outlive every path where the notice could still be lost.
    clearImpersonationMarker();
    dispatch(resetImpersonationExpired());

    // Read from the session rather than assuming an admin: the flag is raised off a
    // storage marker, which is no proof of who is logged in now. An admin lands on
    // /payments, anyone else on their own dashboard instead of an admin-only URL
    // their router has no route for.
    const target = resolveLandingUrl({ activeRole, isAdmin: userInfo.isAdmin });
    if (target) navigate(target, { replace: true });
  }, [impersonationExpired, status, userInfo, activeRole, toast, dispatch, navigate]);

  const handleStopImpersonation = async () => {
    const restored = await dispatch(returnToAdminSession()).unwrap();
    if (restored) {
      dispatch(stopImpersonation());
      toast({ description: "Retour à votre compte" });
    } else {
      terminateSession();
      toast({
        description: "Session d'impersonation expirée",
        variant: "destructive",
      });
    }

    const target = resolveLandingUrl({ activeRole: null, isAdmin: true });
    if (target) navigate(target);
  };

  return {
    isImpersonating,
    stopImpersonation: handleStopImpersonation,
    fetching,
    userInfo: isImpersonating ? userInfo : null,
  };
};
